import { ApiError, newIdempotencyKey, request, storedSite, sessionHeaders } from '../../api';

export interface ShiftRow {
  readonly id: string;
  readonly siteId: string;
  readonly status: 'open' | 'closed' | 'reconciled';
  readonly openedAt: string;
  readonly closedAt: string | null;
  readonly cashOpeningMinor: number;
  readonly cashClosingMinor: number | null;
  readonly collectedMinor: number;
}

export interface CashMovement {
  readonly id: string;
  readonly kind: 'opening' | 'collection' | 'payout' | 'closing';
  readonly amountMinor: number;
  readonly comment: string | null;
  readonly createdAt: string;
}

export interface CashPosition {
  readonly openingMinor: number;
  readonly cashPaymentsMinor: number;
  readonly cashRefundsMinor: number;
  readonly collectedMinor: number;
  readonly payoutMinor: number;
  readonly expectedMinor: number;
}

export interface MethodPosition {
  readonly method: string;
  readonly payments: number;
  readonly capturedMinor: number;
  readonly refunds: number;
  readonly refundedMinor: number;
  readonly netMinor: number;
}

export interface ShiftDetail {
  readonly shift: ShiftRow;
  readonly movements: readonly CashMovement[];
  readonly position: CashPosition;
  readonly byMethod: readonly MethodPosition[];
}

export interface CloseResult {
  readonly shift: ShiftRow;
  readonly position: CashPosition;
  readonly countedMinor: number;
  readonly differenceMinor: number;
  readonly hasDiscrepancy: boolean;
}

export interface ReconcileResult {
  readonly shiftId: string;
  readonly countedMinor: number;
  readonly expectedMinor: number;
  readonly differenceMinor: number;
  readonly status: 'matched' | 'discrepancy';
  readonly comment: string | null;
}

export interface CatalogEntry {
  readonly serviceId: string;
  readonly code: string;
  readonly name: string;
  readonly category: string;
  readonly durationMin: number;
  readonly price: {
    readonly priceMinor: number;
    readonly scope: 'site' | 'network';
    readonly sizeClass: 's' | 'm' | 'l' | 'xl' | null;
    readonly validFrom: string;
  } | null;
}

export interface MaterialRow {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly unit: string;
}

export interface StockRow {
  readonly materialId: string;
  readonly code: string;
  readonly name: string;
  readonly unit: string;
  readonly qty: number;
}

export interface TimesheetEntry {
  readonly id: string;
  readonly employeeId: string;
  readonly shiftId?: string | null;
  readonly comment?: string | null;
  readonly startedAt: string;
  readonly endedAt: string | null;
  readonly minutes: number | null;
  readonly kind: 'shift' | 'manual';
}

export interface EmployeeResult {
  readonly employeeId: string;
  readonly displayName: string;
  readonly minutes: number;
  readonly shifts?: number;
  readonly orders: number;
  readonly revenueMinor: number;
}

function query(site: string, extra: Record<string, string> = {}): string {
  const params = new URLSearchParams({ site_id: site, ...extra });
  return params.toString();
}

export function fetchShifts(siteId: string): Promise<readonly ShiftRow[]> {
  return request<readonly ShiftRow[]>('GET', `/api/v1/pos/shifts?${query(siteId)}`);
}

export function fetchShift(shiftId: string): Promise<ShiftDetail> {
  return request<ShiftDetail>('GET', `/api/v1/pos/shifts/${encodeURIComponent(shiftId)}`);
}

export function openShift(siteId: string, cashOpeningMinor: number): Promise<ShiftRow> {
  return request<ShiftRow>('POST', '/api/v1/pos/shifts/open', { siteId, cashOpeningMinor });
}

export function closeShift(shiftId: string, countedMinor: number): Promise<CloseResult> {
  return request<CloseResult>(
    'POST',
    `/api/v1/pos/shifts/${encodeURIComponent(shiftId)}/close`,
    { countedMinor },
  );
}

export function reconcileShift(shiftId: string, comment?: string): Promise<ReconcileResult> {
  return request<ReconcileResult>(
    'POST',
    `/api/v1/pos/shifts/${encodeURIComponent(shiftId)}/reconcile`,
    comment ? { comment } : {},
  );
}

export function fetchCatalog(siteId: string): Promise<readonly CatalogEntry[]> {
  return request<readonly CatalogEntry[]>('GET', `/api/v1/pos/catalog?${query(siteId)}`);
}

/** `siteId: null` — сетевой тариф: его меняет только роль с сетевой привязкой. */
export function setTariff(body: {
  readonly serviceId: string;
  readonly siteId: string | null;
  readonly priceMinor: number;
}): Promise<{ readonly tariffId: string; readonly priceMinor: number }> {
  return request('POST', '/api/v1/pos/catalog/tariffs', body);
}

export interface ServiceRow {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly category: string;
  readonly durationMin: number;
  readonly status: 'active' | 'archived';
  readonly enabledOnSite: boolean | null;
}

export function fetchServices(siteId: string): Promise<readonly ServiceRow[]> {
  const params = new URLSearchParams({ site_id: siteId, include_archived: '1' });
  return request('GET', `/api/v1/pos/catalog/services?${params}`);
}

export function createService(body: {
  readonly code: string;
  readonly name: string;
  readonly category?: string;
  readonly durationMin?: number;
}): Promise<ServiceRow> {
  return request('POST', '/api/v1/pos/catalog/services', body);
}

export function updateService(
  serviceId: string,
  body: { readonly status?: 'active' | 'archived'; readonly name?: string },
): Promise<ServiceRow> {
  return request('PATCH', `/api/v1/pos/catalog/services/${encodeURIComponent(serviceId)}`, body);
}

export function setServiceOnSite(serviceId: string, siteId: string, enabled: boolean): Promise<unknown> {
  return request('POST', `/api/v1/pos/catalog/services/${encodeURIComponent(serviceId)}/site`, {
    siteId,
    enabled,
  });
}

export interface PackageRow {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly status: 'active' | 'archived';
  readonly networkPriceMinor: number;
  readonly items: readonly { readonly serviceId: string; readonly serviceName: string; readonly qty: number }[];
  readonly price: { readonly priceMinor: number; readonly scope: 'site' | 'network' } | null;
}

export function fetchPackages(siteId: string): Promise<readonly PackageRow[]> {
  const params = new URLSearchParams({ site_id: siteId, include_archived: '1' });
  return request('GET', `/api/v1/pos/catalog/packages?${params}`);
}

export function createPackage(body: {
  readonly code: string;
  readonly name: string;
  readonly priceMinor: number;
  readonly items: readonly { readonly serviceId: string; readonly qty?: number }[];
}): Promise<PackageRow> {
  return request('POST', '/api/v1/pos/catalog/packages', body);
}

export function updatePackage(
  packageId: string,
  body: { readonly status?: 'active' | 'archived'; readonly name?: string },
): Promise<PackageRow> {
  return request('PATCH', `/api/v1/pos/catalog/packages/${encodeURIComponent(packageId)}`, body);
}

export function setPackagePrice(
  packageId: string,
  siteId: string | null,
  priceMinor: number,
): Promise<unknown> {
  return request('POST', `/api/v1/pos/catalog/packages/${encodeURIComponent(packageId)}/price`, {
    siteId,
    priceMinor,
  });
}

export function fetchMaterials(
  siteId: string,
): Promise<{ readonly materials: readonly MaterialRow[]; readonly stock: readonly StockRow[] }> {
  return request('GET', `/api/v1/pos/materials?${query(siteId)}`);
}

export function fetchTimesheet(
  siteId: string,
  from?: string,
  to?: string,
): Promise<{
  readonly entries: readonly TimesheetEntry[];
  readonly results: readonly EmployeeResult[];
}> {
  const extra: Record<string, string> = {};
  if (from) extra['from'] = from;
  if (to) extra['to'] = to;
  return request('GET', `/api/v1/pos/timesheet?${query(siteId, extra)}`);
}

/**
 * Выгрузка табеля за период файлом.
 *
 * Файл отдаёт сервер (`/pos/timesheet/export`): CSV для Excel с итогами по
 * сотрудникам и построчным табелем. Запрос идёт с сессией, поэтому обычная
 * ссылка не годится — файл скачивается через fetch и сохраняется браузером.
 */
export async function downloadTimesheet(siteId: string, from?: string, to?: string): Promise<void> {
  const params = new URLSearchParams({ site_id: siteId });
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const headers: Record<string, string> = {};
  Object.assign(headers, sessionHeaders());
  const site = storedSite();
  if (site) headers['x-moika-site'] = site;

  const res = await fetch(`/api/v1/pos/timesheet/export?${params}`, { headers });
  if (!res.ok) {
    const payload = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    throw new ApiError(
      String(payload['error'] ?? 'E00.internal'),
      String(payload['message'] ?? 'Выгрузка не удалась'),
      res.status,
    );
  }
  const disposition = res.headers.get('content-disposition') ?? '';
  const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'timesheet.csv';
  const url = URL.createObjectURL(await res.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * Инкассация, материалы и табель были доступны только через API: поле
 * «Инкассировано» всегда показывало ноль, вкладка материалов не имела ни одного
 * интерактивного элемента, а минуты в табеле было нечем заполнить.
 */
export function collectCash(
  shiftId: string,
  amountMinor: number,
  comment?: string,
): Promise<{ readonly collectedMinor: number }> {
  return request(
    'POST',
    `/api/v1/pos/shifts/${encodeURIComponent(shiftId)}/collect`,
    { amountMinor, ...(comment ? { comment } : {}) },
    // Движение денег: повторное нажатие не должно инкассировать дважды.
    { idempotencyKey: newIdempotencyKey('collect') },
  );
}

export interface SaveMaterialInput {
  readonly code: string;
  readonly name: string;
  readonly unit?: 'l' | 'ml' | 'kg' | 'g' | 'pc';
  readonly status?: 'active' | 'archived';
}

export function saveMaterial(input: SaveMaterialInput): Promise<MaterialRow> {
  return request('POST', '/api/v1/pos/materials', input);
}

export function recordMovement(input: {
  readonly siteId: string;
  readonly materialId: string;
  readonly kind: 'receipt' | 'writeoff' | 'adjustment';
  readonly qty: number;
  readonly comment?: string;
}): Promise<{ readonly id: string }> {
  return request('POST', '/api/v1/pos/materials/movements', input, {
    idempotencyKey: newIdempotencyKey('movement'),
  });
}

export function setNorm(input: {
  readonly serviceId: string;
  readonly materialId: string;
  readonly qtyPerUnit: number;
  readonly siteId?: string | null;
}): Promise<{ readonly saved: boolean }> {
  return request('POST', '/api/v1/pos/materials/norms', input);
}

export function recordTimesheet(input: {
  readonly employeeId: string;
  readonly siteId: string;
  readonly startedAt: string;
  readonly endedAt: string;
  readonly comment?: string;
}): Promise<unknown> {
  return request('POST', '/api/v1/pos/timesheet', input);
}

export interface CashierOption {
  readonly employeeId: string;
  readonly displayName: string;
}

export function fetchCashiers(): Promise<readonly CashierOption[]> {
  return request('GET', '/api/v1/pos/cashiers');
}

/**
 * История цены услуги.
 *
 * spec-7 §4.1 требует, чтобы у тарифа была история, и она есть: `tariff` хранит
 * `valid_from`/`valid_to`, а маршрут отдаёт весь ряд. Экран показывал только
 * действующую цену — то есть на вопрос «почему в марте пробили другую сумму»
 * ответить было нечем.
 */
export interface PriceHistoryRow {
  readonly tariffId: string;
  readonly serviceId: string;
  readonly priceMinor: number;
  readonly currency: string;
  readonly scope: 'site' | 'network';
  readonly sizeClass: 's' | 'm' | 'l' | 'xl' | null;
  readonly validFrom: string;
  readonly validTo: string | null;
  readonly closed: boolean;
}

export function priceHistory(
  serviceId: string,
  siteId: string | null,
): Promise<readonly PriceHistoryRow[]> {
  const params = new URLSearchParams({ service_id: serviceId });
  if (siteId) params.set('site_id', siteId);
  return request<readonly PriceHistoryRow[]>('GET', `/api/v1/pos/catalog/history?${params}`);
}

/** Кассовый терминал на учёте. Токен показывается один раз — при постановке. */
export interface PosDevice {
  readonly deviceId: string;
  readonly siteId: string;
  readonly label: string;
  readonly appVersion: string | null;
  readonly queueDepth: number;
  readonly status: 'active' | 'revoked';
  readonly lastSeenAt: string | null;
}

/**
 * Терминалы объекта.
 *
 * Объект передаётся параметром, а не только заголовком: заголовок берётся из
 * `storedSite()` и был бы верным, но запрос без явной зависимости просто не
 * повторялся при переключении объекта — список оставался от первого. Явный
 * параметр делает зависимость видимой в коде.
 */
export function fetchPosDevices(siteId?: string | null): Promise<readonly PosDevice[]> {
  const query = siteId ? `?site_id=${encodeURIComponent(siteId)}` : '';
  return request<readonly PosDevice[]>('GET', `/api/v1/pos/devices${query}`);
}

export function enrollPosDevice(body: {
  readonly siteId: string;
  readonly label?: string;
}): Promise<{ readonly deviceId: string; readonly token: string }> {
  return request('POST', '/api/v1/pos/devices', body);
}

export function revokePosDevice(deviceId: string): Promise<{ readonly revoked: boolean }> {
  return request('DELETE', `/api/v1/pos/devices/${encodeURIComponent(deviceId)}`);
}

/** Оплата, требующая человека: вернуть деньги клиенту или выяснить итог. */
export interface AttentionPayment {
  readonly paymentId: string;
  readonly orderId: string;
  readonly siteId: string;
  readonly method: string;
  readonly amountMinor: number;
  readonly provider: string | null;
  readonly externalId: string | null;
  readonly kind: 'refund_required' | 'unresolved';
  readonly reason: string | null;
  readonly createdAt: string;
}

export async function fetchAttentionPayments(siteId: string): Promise<readonly AttentionPayment[]> {
  const res = await request<{ readonly payments: readonly AttentionPayment[] }>(
    'GET',
    `/api/v1/pos/payments/attention?${query(siteId)}`,
  );
  return res.payments;
}

export function resolveAttentionPayment(
  paymentId: string,
  body: { readonly reference: string; readonly comment?: string },
): Promise<unknown> {
  return request('POST', `/api/v1/pos/payments/${encodeURIComponent(paymentId)}/resolve`, body);
}
